import { useState, useMemo } from 'react';

import { useValidationService } from '../../../../../services/hooks';
import { Dialog, DialogContent } from '../../../../../Dialog';
import { Button } from '../../../../../Button';
import { Typography } from '../../../../../Typography';
import { Box } from '../../../../../Box';
import { FileInputValue, FileInput, TextInput } from '../../../../../input';
import { useForm } from '../../hooks';

interface AddImageDialogProps {
  open: boolean;
  onClose: () => void;
  addImage: (
    file: FileInputValue,
    description: string | undefined,
  ) => Promise<void>;
}

interface AddImageDialogForm {
  image: FileInputValue;
}

const MIN_IMAGE_WIDTH = 160;
const MIN_IMAGE_HEIGHT = 90;

export const AddImageDialog = ({
  open,
  onClose,
  addImage,
}: AddImageDialogProps) => {
  const [image, setImage] = useState<FileInputValue>();
  const [description, setDescription] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const validator = useValidationService();

  const allValues = useMemo(() => {
    return {
      image,
    };
  }, [image]);

  const fieldValidators = useMemo(() => {
    return {
      image: [
        validator.required<AddImageDialogForm>(),
        validator.sizeLimit<AddImageDialogForm>(
          'Загрузите файл jpeg, png, webp, tiff, dng или heif до 10МБ',
          {
            field: `image`,
            sizeLimit: 10,
          },
        ),
        validator.requiredIfFile<AddImageDialogForm>(
          'Загрузите файл jpeg, png, tiff, webp, dng или heif до 10МБ',
          {
            field: 'image',
            fileTypes: [
              'image/jpeg',
              'image/png',
              'image/heif',
              'image/heic',
              'image/webp',
              'image/dng',
              'image/tiff',
            ],
          },
        ),
        validator.imageResolutionCheck(
          `Размер изображения не может быть меньше ${MIN_IMAGE_WIDTH} x ${MIN_IMAGE_HEIGHT}`,
          {
            minWidth: MIN_IMAGE_WIDTH,
            minHeight: MIN_IMAGE_HEIGHT,
          },
        ),
      ],
    };
  }, [validator]);

  const { errors, touched, validateField, hasValidationErrors, showError } =
    useForm<AddImageDialogForm>(allValues, fieldValidators);

  const handleChangeImageInput = async (value?: FileInputValue | undefined) => {
    setImage(value);
    await validateField('image', value);
    showError('image');
  };

  const handleChangeDescriptionInput = (value?: string) => {
    setDescription(() => value || '');
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      if (image) {
        await addImage(image, description);
      }
    } finally {
      setIsSubmitting(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <DialogContent
          title="Добавление изображения"
          onClose={onClose}
          actions={
            <>
              <Button
                variant="tertiaryLight"
                size="large"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Отменить
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="large"
                disabled={hasValidationErrors || isSubmitting}
                loading={isSubmitting}
              >
                Добавить
              </Button>
            </>
          }
        >
          <Box display="flex" flexDirection="column" gap="24">
            <Typography variant="body1" color="text.light.secondary">
              Отображаться изображения будут в соотношении сторон 16:9
            </Typography>

            <Box display="flex" flexDirection="column" gap="8">
              <FileInput
                fullWidth
                required
                value={image}
                label="Изображение"
                placeholder="Загрузите файл jpeg, png, tiff, webp, dng или heif до 10МБ"
                helperText="Размер изображения от 160 × 90 пикселей"
                onChange={handleChangeImageInput}
                error={touched.image && !!errors.image}
                errorText={touched.image ? errors.image : undefined}
              />
              <TextInput
                name="description"
                label="Подпись"
                placeholder="Подпись к изображению"
                onChange={handleChangeDescriptionInput}
              />
            </Box>
          </Box>
        </DialogContent>
      </form>
    </Dialog>
  );
};
